import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import donutBg from '../../../../assets/Circle.png';
import { generateTestId } from '../../../../app/functions';
import { getMediaPath } from '../../../../app/functions';
const ChartContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '350px',
  height: '365px',
  margin: '0 auto',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
    height: '450px',
  },
  [theme.breakpoints.up('md')]: {
    width: '400px',
    height: '400px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '550px',
    height: '450px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '540px',
    height: '540px',
  }
}));

const DoughnutBase = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '297.278px',
  height: '297.278px',
  borderRadius: '297.278px',
  background: `url(${donutBg})`,
  backgroundSize: '100% 100%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  [theme.breakpoints.up('sm')]: {
    width: '380px',
    height: '380px',
    borderRadius: '380px',
  },
  [theme.breakpoints.up('md')]: {
    width: '300px',
    height: '300px',
    borderRadius: '250px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '350px',
    height: '350px',
    borderRadius: '300px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '490px',
    height: '490px',
    borderRadius: '490px',
  }
}));

const DoughnutCenter = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '200px',
  height: '200px',
  borderRadius: '50%',
  background: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  boxShadow: 'inset 0px 4px 44px rgba(48, 94, 138, 0.1)',
  [theme.breakpoints.up('sm')]: {
    width: '250px',
    height: '250px',
    gap: '4px',
  },
  [theme.breakpoints.up('md')]: {
    width: '200px',
    height: '200px',
    gap: '6px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '250px',
    height: '250px',
    gap: '8px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '340px',
    height: '340px',
    gap: '10px',
  }
}));

const CenterText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.purple,
  fontSize: '16px',
  fontWeight: 700,
  textAlign: 'center',
  lineHeight: '100%',
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px',
    lineHeight: 'normal',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    fontWeight: 600,
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '22px',
    fontWeight: 500,
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '24px',
    fontWeight: 500,
  }
}));

const CenterAmount = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '36px',
  fontWeight: 700,
  lineHeight: 'normal',
  [theme.breakpoints.up('sm')]: {
    fontSize: '42px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '30px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '45px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '60px',
    letterSpacing: '-0.6px',
  }
}));

const CurrencySymbol = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: 'normal',
  marginBottom: '5px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '24px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    marginBottom: '2px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '24px',
    marginBottom: '5px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '28px',
    marginBottom: '10px',
  }
}));

const AmountWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: '4px',
  position: 'relative',
  paddingBottom: '4px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '0',
    right: '0',
    width: '100%',
    height: '1px',
    background: theme.palette.text.primary
  },
  [theme.breakpoints.up('sm')]: {
    paddingBottom: '4px',
    '&::after': {
      height: '1px'
    }
  },
  [theme.breakpoints.up('md')]: {
    paddingBottom: '6px',
    '&::after': {
      height: '1.5px'
    }
  },
  [theme.breakpoints.up('lg')]: {
    paddingBottom: '8px',
    '&::after': {
      height: '2px'
    }
  }
}));

const SliceContent = styled(Box)(({ rotation, theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '80.036px',
  height: '80.036px',
  textAlign: 'center',
  transform: `rotate(${rotation}deg) translateY(0) rotate(-${rotation}deg)`,
  background: theme.palette.common.white,
  borderRadius: '57.169px',
  padding: '13.721px 0px',
  boxShadow: '0px 2.287px 25.154px 0px rgba(48, 94, 138, 0.20)',
  gap: '1px',
  [theme.breakpoints.up('sm')]: {
    width: '100px',
    height: '100px',
    borderRadius: '70px',
    padding: '18px 0px',
    gap: '2px',
  },
  [theme.breakpoints.up('md')]: {
    width: '80px',
    height: '80px',
    borderRadius: '70px',
    padding: '18px 0px',
    gap: '3px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '95px',
    height: '95px',
    borderRadius: '70px',
    padding: '20px 0px',
    gap: '3px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '130px',
    height: '130px',
    borderRadius: '90px',
    padding: '22px 0px',
    gap: '4px',
  }
}));

// Create styled components for each slice position
const SliceTop = styled(SliceContent)(({ theme }) => ({
  top: '-4%',
  right: '47%',
  [theme.breakpoints.up('sm')]: {
    top: '-3%',
    right: '47%',
  },
  [theme.breakpoints.up('md')]: {
    top: '0%',
    right: '48%',
  },
  [theme.breakpoints.up('lg')]: {
    top: '-3%',
    right: '50%',
  },
  [theme.breakpoints.up('xl')]: {
    top: '-8%',
    right: '48%',
  }
}));

const SliceRight = styled(SliceContent)(({ theme }) => ({
  top: '35%',
  right: '82%',
  [theme.breakpoints.up('sm')]: {
    top: '32%',
    right: '82%',
  },
  [theme.breakpoints.up('md')]: {
    top: '35%',
    right: '80%',
  },
  [theme.breakpoints.up('lg')]: {
    top: '35%',
    right: '77%',
  },
  [theme.breakpoints.up('xl')]: {
    top: '35%',
    right: '85%',
  }
}));

const SliceBottomRight = styled(SliceContent)(({ theme }) => ({
  top: '78%',
  right: '60%',
  [theme.breakpoints.up('sm')]: {
    top: '77%',
    right: '56%',
  },
  [theme.breakpoints.up('md')]: {
    top: '75%',
    right: '58%',
  },
  [theme.breakpoints.up('lg')]: {
    top: '78%',
    right: '55%',
  },
  [theme.breakpoints.up('xl')]: {
    top: '83%',
    right: '55%',
  }
}));

const SliceBottomLeft = styled(SliceContent)(({ theme }) => ({
  top: '65%',
  right: '5%',
  [theme.breakpoints.up('sm')]: {
    top: '62%',
    right: '2%',
  },
  [theme.breakpoints.up('md')]: {
    top: '60%',
    right: '5%',
  },
  [theme.breakpoints.up('lg')]: {
    top: '63%',
    right: '13%',
  },
  [theme.breakpoints.up('xl')]: {
    top: '65%',
    right: '-3%',
  }
}));

const SliceLeft = styled(SliceContent)(({ theme }) => ({
  top: '10%',
  right: '5%',
  [theme.breakpoints.up('sm')]: {
    top: '13%',
    right: '3%',
  },
  [theme.breakpoints.up('md')]: {
    top: '15%',
    right: '8%',
  },
  [theme.breakpoints.up('lg')]: {
    top: '13%',
    right: '13%',
  },
  [theme.breakpoints.up('xl')]: {
    top: '10%',
    right: '-2%',
  }
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '24px',
    height: '24px',
  },
  [theme.breakpoints.up('sm')]: {
    width: '30px',
    height: '30px',
    '& img': {
      width: '30px',
      height: '30px',
    }
  },
  [theme.breakpoints.up('md')]: {
    width: '26px',
    height: '26px',
    '& img': {
      width: '26px',
      height: '26px',
    }
  },
  [theme.breakpoints.up('lg')]: {
    width: '28px',
    height: '28px',
    '& img': {
      width: '28px',
      height: '28px',
    }
  },
  [theme.breakpoints.up('xl')]: {
    width: '30px',
    height: '30px',
    '& img': {
      width: '30px',
      height: '30px',
    }
  }
}));

const SliceText = styled(Typography)(({ theme }) => ({
  color: theme.palette.doughnutChart.hover,
  fontSize: '10px',  
  fontWeight: 400,
  whiteSpace: 'nowrap',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  margin: 0,
  marginTop: '0px',
  '&.amount': {
    color: theme.palette.doughnutChart.accent,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    marginTop: '2px',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '11px',
    marginTop: '1px',
    '&.amount': {
      fontSize: '13px',
      marginTop: '3px',
    }
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '10px',
    marginTop: '1px',
    '&.amount': {
      fontSize: '10px',
      marginTop: '3px',
    }
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '11px',
    marginTop: '2px',
    '&.amount': {
      fontSize: '12px',
      marginTop: '4px',
    }
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '14px',
    marginTop: '3px',
    '&.amount': {
      fontSize: '16px',
      marginTop: '5px',
    }
  }
}));

const DoughnutChart = ({ slices, centerText = "בשנה תוכלו לחסוך עד", totalAmount = 5000, savingsAmount = 0, testID }) => {
  const theme = useTheme();
  // Calculate total of all slice amounts
  const total = slices.reduce((sum, slice) => sum + slice.amount, 0);
  
  // Calculate the percentage and degree for each slice
  let currentDegree = 0;
  const slicesWithDegrees = slices.map((slice, index) => {
    const percentage = (slice.amount / total) * 100;
    const degrees = (percentage / 100) * 360;
    const startDegree = currentDegree;
    currentDegree += degrees;
    
    return {
      ...slice,
      percentage,
      startDegree,
      degrees,
      icon: slice.icon
    };
  });

  // Map slice components to their positions
  const SliceComponents = [SliceTop, SliceRight, SliceBottomRight, SliceBottomLeft, SliceLeft];

  return (
    <ChartContainer testID={generateTestId(testID, 'doughnut-chart')}>
      <DoughnutBase testID={generateTestId(testID, 'doughnut-base')} />
      <DoughnutCenter testID={generateTestId(testID, 'doughnut-center')}>
        <CenterText testID={generateTestId(testID, 'center-text')}>{centerText}</CenterText>
        <AmountWrapper testID={generateTestId(testID, 'amount-wrapper')}>
          <CenterAmount testID={generateTestId(testID, 'center-amount')}>{savingsAmount.toLocaleString()}</CenterAmount>
          <CurrencySymbol testID={generateTestId(testID, 'currency-symbol')}>₪</CurrencySymbol>
        </AmountWrapper>
      </DoughnutCenter>
      
      {/* Render slices using the appropriate styled component for each position */}
      {slicesWithDegrees.map((slice, index) => {
        const SliceComponent = SliceComponents[index];
        return (
          <SliceComponent 
            key={`slice-${index}`}
            id={`${index}-${slice.text}`}
            rotation={slice.startDegree + (slice.degrees / 2)}
            testID={generateTestId(testID, `slice-content-${index}`)}
          >
            <IconContainer testID={generateTestId(testID, `icon-container-${index}`)}>
              <img src={getMediaPath(slice.icon)} alt={slice.text} />
            </IconContainer>
            <SliceText testID={generateTestId(testID, `slice-text-${index}`)}>{slice.text}</SliceText>
            <SliceText className="amount" testID={generateTestId(testID, `slice-amount-${index}`)}>₪{slice.amount.toLocaleString()}</SliceText>
          </SliceComponent>
        );
      })}
    </ChartContainer>
  );
};

export default DoughnutChart; 